<template>
  <Teleport to="body">
    <div v-if="open" v-show="!emitting" class="task-dialog" @mousedown.self="close">
      <div class="task-dialog__card" :class="{ 'is-detail': view !== 'list' }" role="dialog" aria-modal="true" aria-label="Tarefas">
        <header class="task-dialog__head">
          <h3>{{ heading }}</h3>
          <button type="button" class="task-dialog__close" title="Fechar" @click="close">
            <i class="bi bi-x-lg"></i>
          </button>
        </header>

        <div v-if="view === 'list'" class="task-dialog__body task-dialog__split">
          <section class="task-list">
            <div class="task-chip-group">
              <button type="button" class="task-chip" :class="{ 'is-on': box === 'inbox' }" @click="setBox('inbox')">Para mim / respostas</button>
              <button type="button" class="task-chip" :class="{ 'is-on': box === 'sent' }" @click="setBox('sent')">Que eu criei</button>
            </div>

            <p v-if="loading" class="task-muted">Carregando...</p>
            <p v-else-if="!tasks.length" class="task-muted">Nenhuma tarefa nesta lista.</p>
            <div v-for="task in tasks" :key="task.id" class="task-row" :class="'task-row--' + task.status">
              <button type="button" class="task-row__open" @click="openTask(task.id)">
                <span class="task-row__heading">
                  <span class="task-row__icon" aria-hidden="true"><i class="bi" :class="statusIcon(task.status)"></i></span>
                  <strong>{{ task.title }}</strong>
                </span>
                <span class="task-row__meta">
                  <span>{{ typeLabel(task.type) }}</span>
                  <span class="task-status" :class="'task-status--' + task.status">{{ statusLabel(task.status) }}</span>
                  <span class="task-row__who">{{ box === 'inbox' && task.status !== 'answered' ? task.author?.name : task.assignee?.name }}</span>
                </span>
                <span v-if="task.patient" class="task-row__patient"><i class="bi bi-person" aria-hidden="true"></i> {{ task.patient.name }}</span>
                <span v-if="task.body" class="task-row__body">{{ task.body }}</span>
              </button>
              <button
                v-if="canCancelTask(task)"
                type="button"
                class="task-row__cancel"
                title="Cancelar tarefa"
                :disabled="saving"
                @click="askCancel(task)"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </section>

          <form class="task-form" @submit.prevent="createTask">
            <h4>Nova tarefa</h4>
            <div class="task-form__grid">
              <label class="task-field">
                Tipo
                <SelectUbbi v-model="form.type" :options="typeOptions" placeholder="Escolha o tipo" />
              </label>
              <label class="task-field">
                Prazo
                <input v-model="form.due_at" type="date" class="form-control" />
              </label>
            </div>
            <label class="task-field">
              Para quem
              <SelectUbbi
                v-model="form.assignee_id"
                :options="userOptions"
                :placeholder="usersLoading ? 'Carregando...' : (typeIsClinical ? 'Escolha o profissional' : 'Escolha a pessoa')"
                :disabled="usersLoading"
                searchable
                show-clear
              />
            </label>
            <p v-if="typeIsClinical" class="task-hint">Este tipo só pode ir para um profissional.</p>
            <label class="task-field">
              Paciente <template v-if="typeIsClinical">*</template>
              <SelectUbbi
                v-model="form.patient_id"
                :options="patientOptions"
                placeholder="Pesquisar paciente"
                searchable
                show-clear
                @search="onPatientSearch"
              />
            </label>
            <label class="task-field">
              Título
              <input v-model="form.title" type="text" class="form-control" maxlength="160" placeholder="Ex.: Renovar a receita" />
            </label>
            <label class="task-field">
              Descrição
              <textarea v-model="form.body" class="form-control" rows="3" placeholder="Descreva o que precisa ser feito"></textarea>
            </label>
            <label class="task-field">
              Anexos
              <span class="task-file">
                <input type="file" multiple @change="onFiles" />
                <i class="bi bi-paperclip"></i>
                <span>{{ fileLabel(form.files) }}</span>
              </span>
            </label>
          </form>
        </div>

        <div v-else class="task-dialog__body task-dialog__detail">
          <p v-if="!selected" class="task-muted">Carregando...</p>
          <div v-else class="task-detail">
            <button type="button" class="task-chip" @click="backToList">
              <i class="bi bi-arrow-left"></i>
              Voltar
            </button>
            <header class="task-detail__head">
              <p class="task-kicker">
                {{ typeLabel(selected.type) }}
                <span class="task-status" :class="'task-status--' + selected.status">{{ statusLabel(selected.status) }}</span>
              </p>
              <h4>{{ selected.title }}</h4>
              <p v-if="selected.patient" class="task-muted">{{ selected.patient.name }}</p>
            </header>
            <article
              v-for="message in selected.messages"
              :key="message.id"
              class="task-message"
              :class="{ 'task-message--reply': message.kind !== 'request' }"
            >
              <header>
                <strong>{{ message.kind === 'request' ? 'Pedido' : 'Resposta' }}</strong>
                <span>{{ message.author?.name }}</span>
              </header>
              <p v-if="message.body">{{ message.body }}</p>
              <div v-if="message.files?.length" class="task-files">
                <button
                  v-for="file in message.files"
                  :key="file.id"
                  type="button"
                  class="task-chip"
                  @click="downloadFile(file)"
                >
                  <i class="bi bi-paperclip"></i>
                  {{ file.name }}
                </button>
              </div>
            </article>

            <section v-if="selected.documents?.length" class="task-message">
              <strong>Documentos vinculados</strong>
              <p class="task-muted">Os documentos abaixo acompanham a resposta automaticamente.</p>
              <div class="task-files">
                <button v-for="file in selected.documents" :key="file.id" type="button" class="task-chip" @click="downloadFile(file)">
                  <i class="bi bi-file-earmark-pdf"></i>{{ file.name }}
                </button>
              </div>
            </section>
            <p v-if="selected.reply_all || selected.reply_to" class="task-muted">
              Resposta para: {{ selected.reply_all ? 'Todos da clínica' : selected.reply_to.name }}
            </p>
            <form v-if="canReply" class="task-reply" @submit.prevent="reply">
              <label class="task-field">
                Enviar resposta para
                <SelectUbbi v-model="replyRecipient" :options="replyRecipientOptions" :disabled="usersLoading" searchable placeholder="Escolha quem recebe" />
              </label>
              <p v-if="replyRecipient === 'all'" class="task-hint">Todos os usuários ativos desta clínica poderão acessar a tarefa, os anexos e reabrir o pedido.</p>
              <label class="task-field">
                Resposta
                <textarea v-model="replyBody" class="form-control" rows="3" placeholder="Escreva a resposta"></textarea>
              </label>
              <label class="task-field">
                Anexo
                <span class="task-file">
                  <input type="file" multiple @change="onReplyFiles" />
                  <i class="bi bi-paperclip"></i>
                  <span>{{ fileLabel(replyFiles) }}</span>
                </span>
              </label>
            </form>
          </div>
        </div>

        <footer class="task-dialog__foot">
          <template v-if="view === 'list'">
            <button type="button" class="task-chip" @click="close">Fechar</button>
            <button type="button" class="task-chip task-chip--primary" :disabled="saving" @click="createTask">
              {{ saving ? 'Salvando...' : 'Criar tarefa' }}
            </button>
          </template>
          <template v-else-if="selected">
            <button v-if="canCancel" type="button" class="task-chip task-chip--danger" :disabled="saving" @click="askCancel(selected)">Cancelar tarefa</button>
            <button v-if="canReopen" type="button" class="task-chip" :disabled="saving" @click="reopenTask">Reabrir</button>
            <button v-if="canOpenChart" type="button" class="task-chip" @click="openChart">Abrir prontuário</button>
            <div v-if="canEmit" class="task-split" @focusout="closeEmissionMenu" @keydown.esc.stop="emissionMenu = false">
              <button type="button" class="task-chip task-chip--emission task-split__main" @click="startEmission('reissue')">{{ reissueLabel }}</button>
              <button type="button" class="task-chip task-chip--emission task-split__toggle" :aria-expanded="emissionMenu" aria-haspopup="menu"
                      :aria-label="'Mais opções de ' + emissionLabel.toLowerCase()" @click="emissionMenu = !emissionMenu">
                <i class="bi bi-chevron-down" aria-hidden="true"></i>
              </button>
              <div v-if="emissionMenu" class="task-split__menu" role="menu">
                <button type="button" role="menuitem" @click="startEmission('emit')">{{ emissionLabel }}</button>
              </div>
            </div>
            <button v-if="canReply" type="button" class="task-chip task-chip--primary" :disabled="saving" @click="reply">
              {{ saving ? 'Enviando...' : 'Responder' }}
            </button>
          </template>
        </footer>
      </div>

      <div v-if="pendingCancel" class="task-confirm" @mousedown.self="pendingCancel = null">
        <div class="task-confirm__card" role="dialog" aria-modal="true" aria-label="Cancelar tarefa">
          <h3>Cancelar tarefa</h3>
          <p>Cancelar “{{ pendingCancel.title }}”?</p>
          <footer>
            <button type="button" class="task-chip" @click="pendingCancel = null">Voltar</button>
            <button type="button" class="task-chip task-chip--danger" :disabled="saving" @click="confirmCancel">
              {{ saving ? 'Cancelando...' : 'Cancelar tarefa' }}
            </button>
          </footer>
        </div>
      </div>
    </div>
    <TaskEmissionDialog v-if="open && emitting" :task="selected" :mode="emitting" @close="closeEmission" @completed="emissionCompleted" />
  </Teleport>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import http from '@/helpers/http';
import alert from '@/helpers/alert';
import permission from '@/helpers/permission';
import SelectUbbi from '@/components/SelectUbbi.vue';
import { TASK_STATUS, TASK_TYPES, taskError, taskTypeLabel } from './taskOptions';

function dueInDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

const emptyForm = () => ({
  assignee_id: null,
  patient_id: null,
  type: 'question',
  title: '',
  body: '',
  due_at: dueInDays(5),
  files: [],
});

export default {
  name: 'TaskModal',
  components: { SelectUbbi, TaskEmissionDialog: defineAsyncComponent(() => import('./TaskEmissionDialog.vue')) },
  props: {
    open: { type: Boolean, default: false },
    focusId: { default: null },
  },
  emits: ['close', 'changed'],
  data() {
    return {
      emitting: null,
      emissionMenu: false,
      box: 'inbox',
      view: 'list',
      tasks: [],
      users: [],
      patients: [],
      patientName: '',
      selected: null,
      form: emptyForm(),
      replyBody: '',
      replyRecipient: null,
      replyFiles: [],
      loading: false,
      usersLoading: false,
      saving: false,
      patientTimer: null,
      pendingCancel: null,
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    userAuth() {
      return this.$store.state.auth.user;
    },
    heading() {
      return this.view === 'detail' && this.selected ? this.selected.title : 'Tarefas';
    },
    userOptions() {
      return this.users
        .filter((user) => user.id_user !== this.userAuth?.id_user)
        .filter((user) => !this.typeIsClinical || user.id_prof)
        .map((user) => ({ value: user.id_user, label: user.user_name }));
    },
    replyRecipientOptions() {
      return [
        { value: 'all', label: 'Todos da clínica' },
        ...this.users.filter((user) => user.id_user !== this.userAuth?.id_user && user.user_active !== false)
          .map((user) => ({ value: user.id_user, label: user.user_name })),
      ];
    },
    patientOptions() {
      const options = this.patients.map((patient) => ({
        value: patient.id_patient,
        label: patient.patient_name,
      }));
      if (this.form.patient_id && !options.some((option) => Number(option.value) === Number(this.form.patient_id))) {
        options.unshift({ value: this.form.patient_id, label: this.patientName || 'Paciente' });
      }
      return options;
    },
    typeOptions() {
      return TASK_TYPES.map((type) => ({ value: type.value, label: type.label }));
    },
    typeIsClinical() {
      return TASK_TYPES.find((type) => type.value === this.form.type)?.clinical === true;
    },
    canReply() {
      return this.selected?.status === 'open' && this.selected?.assignee?.id === this.userAuth?.id_user;
    },
    canCancel() {
      return this.selected?.status === 'open' && this.selected?.author?.id === this.userAuth?.id_user;
    },
    canReopen() {
      return this.selected?.status === 'answered' && (this.selected?.author?.id === this.userAuth?.id_user
        || this.selected?.reply_to?.id === this.userAuth?.id_user || this.selected?.reply_all);
    },
    emissionLabel() {
      return { prescription: 'Emitir prescrição', exam_request: 'Fazer pedido de exame', document: 'Emitir documento' }[this.selected?.type];
    },
    reissueLabel() {
      return { prescription: 'Reemitir última prescrição', exam_request: 'Reemitir último pedido de exame', document: 'Reemitir último documento' }[this.selected?.type];
    },
    canEmit() { return this.canReply && this.canOpenChart && Boolean(this.emissionLabel); },
    canOpenChart() {
      return Boolean(this.selected?.patient?.id)
        && Boolean(this.userAuth?.id_prof)
        && permission.has('List Medical Record');
    },
  },
  watch: {
    open(value) {
      document.body.classList.toggle('task-dialog-lock', Boolean(value));
      if (!value) {
        this.emitting = null;
        this.emissionMenu = false;
        this.pendingCancel = null;
        return;
      }
      this.view = 'list';
      this.selected = null;
      this.form = emptyForm();
      this.loadUsers();
      this.loadTasks().then(() => {
        if (this.focusId) {
          this.openTask(this.focusId);
        }
      });
    },
    focusId(id) {
      if (this.open && id) {
        this.openTask(id);
      }
    },
    'form.patient_id'(id) {
      const patient = this.patients.find((item) => Number(item.id_patient) === Number(id));
      if (patient) {
        this.patientName = patient.patient_name;
      }
      if (!id) {
        this.patientName = '';
      }
    },
    'form.type'() {
      if (!this.users.length) {
        this.loadUsers();
      }
      const selected = this.users.find((user) => Number(user.id_user) === Number(this.form.assignee_id));
      const stillValid = selected && (!this.typeIsClinical || selected.id_prof);
      if (!stillValid) {
        this.form.assignee_id = null;
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKey);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKey);
    document.body.classList.remove('task-dialog-lock');
    clearTimeout(this.patientTimer);
  },
  methods: {
    emissionCompleted(task) {
      this.selected = task;
      this.$emit('changed');
    },
    startEmission(mode) {
      this.emissionMenu = false;
      this.emitting = mode;
    },
    closeEmissionMenu(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) this.emissionMenu = false;
    },
    closeEmission() {
      this.emitting = null;
      this.openTask(this.selected.id);
    },
    typeLabel: taskTypeLabel,
    statusLabel(status) {
      return status === 'open' ? 'Pendente' : (TASK_STATUS[status] || status);
    },
    statusIcon(status) {
      return { open: 'bi-clock-history', answered: 'bi-check-circle-fill', cancelled: 'bi-x-circle' }[status] || 'bi-list-task';
    },
    fileLabel(files) {
      if (!files?.length) {
        return 'Anexar arquivos';
      }
      if (files.length === 1) {
        return files[0].name;
      }
      return `${files.length} arquivos`;
    },
    close() {
      this.$emit('close');
    },
    onKey(event) {
      if (!this.open || this.emitting || event.key !== 'Escape') {
        return;
      }
      if (this.pendingCancel) {
        this.pendingCancel = null;
        return;
      }
      this.close();
    },
    setBox(box) {
      this.box = box;
      this.loadTasks();
    },
    loadUsers() {
      this.usersLoading = true;
      http.get(`/places/${this.place.id_place}/users`).then((response) => {
        const list = response.data?.data ?? response.data;
        this.users = Array.isArray(list) ? list : [];
      }).catch(() => {
        this.users = [];
      }).finally(() => {
        this.usersLoading = false;
      });
    },
    loadTasks() {
      this.loading = true;
      return http.get(`/places/${this.place.id_place}/tasks`, { params: { box: this.box } })
        .then((response) => {
          this.tasks = response.data || [];
        })
        .catch((error) => {
          this.tasks = [];
          alert.toastError(taskError(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openTask(id) {
      this.view = 'detail';
      this.selected = null;
      this.replyBody = '';
      this.replyFiles = [];
      http.get(`/places/${this.place.id_place}/tasks/${id}`).then((response) => {
        this.selected = response.data;
        this.replyRecipient = this.selected.reply_all ? 'all' : (this.selected.reply_to?.id ?? this.selected.author?.id);
      }).catch((error) => {
        alert.toastError(taskError(error));
        this.view = 'list';
      });
    },
    backToList() {
      this.view = 'list';
      this.selected = null;
      this.loadTasks();
    },
    onPatientSearch(query) {
      clearTimeout(this.patientTimer);
      const search = String(query || '').trim();
      if (search.length < 2) {
        return;
      }
      this.patientTimer = setTimeout(() => this.searchPatients(search), 300);
    },
    searchPatients(search) {
      this.patientSearch = search;
      http.get(`/places/${this.place.id_place}/patients/autocomplete`, { params: { search, limit: 8 } })
        .then((response) => {
          if (this.patientSearch !== search) {
            return;
          }
          this.patients = response.data?.data || [];
          const selected = this.patients.find((patient) => Number(patient.id_patient) === Number(this.form.patient_id));
          if (selected) {
            this.patientName = selected.patient_name;
          }
        });
    },
    onFiles(event) {
      this.form.files = Array.from(event.target.files || []);
    },
    onReplyFiles(event) {
      this.replyFiles = Array.from(event.target.files || []);
    },
    createTask() {
      if (this.saving) {
        return;
      }
      this.saving = true;
      const payload = new FormData();
      payload.append('assignee_id', this.form.assignee_id ?? '');
      payload.append('type', this.form.type);
      payload.append('title', this.form.title);
      if (this.form.body) payload.append('body', this.form.body);
      if (this.form.due_at) payload.append('due_at', this.form.due_at);
      if (this.form.patient_id) payload.append('patient_id', this.form.patient_id);
      this.form.files.forEach((file) => payload.append('files[]', file));

      http.post(`/places/${this.place.id_place}/tasks`, payload)
        .then(() => {
          alert.toastSuccess('Tarefa criada.');
          this.form = emptyForm();
          this.patientName = '';
          this.box = 'sent';
          this.$emit('changed');
          return this.loadTasks();
        })
        .catch((error) => alert.toastError(taskError(error)))
        .finally(() => {
          this.saving = false;
        });
    },
    reply() {
      if (this.saving || !this.selected) {
        return;
      }
      this.saving = true;
      const payload = new FormData();
      payload.append('reply_all', this.replyRecipient === 'all' ? '1' : '0');
      if (this.replyRecipient !== 'all') payload.append('recipient_id', this.replyRecipient ?? '');
      if (this.replyBody) payload.append('body', this.replyBody);
      this.replyFiles.forEach((file) => payload.append('files[]', file));

      http.post(`/places/${this.place.id_place}/tasks/${this.selected.id}/reply`, payload)
        .then((response) => {
          this.selected = response.data;
          this.replyBody = '';
          this.replyFiles = [];
          this.$emit('changed');
          alert.toastSuccess('Resposta enviada.');
        })
        .catch((error) => alert.toastError(taskError(error)))
        .finally(() => {
          this.saving = false;
        });
    },
    canCancelTask(task) {
      return task?.status === 'open' && task?.author?.id === this.userAuth?.id_user;
    },
    askCancel(task) {
      if (this.saving || !this.canCancelTask(task)) {
        return;
      }
      this.pendingCancel = task;
    },
    confirmCancel() {
      const task = this.pendingCancel;
      if (this.saving || !this.canCancelTask(task)) {
        return;
      }
      this.saving = true;
      http.post(`/places/${this.place.id_place}/tasks/${task.id}/cancel`)
        .then((response) => {
          if (this.selected?.id === task.id) {
            this.selected = response.data;
          }
          this.pendingCancel = null;
          this.$emit('changed');
          alert.toastSuccess('Tarefa cancelada.');
          return this.loadTasks();
        })
        .catch((error) => alert.toastError(taskError(error)))
        .finally(() => {
          this.saving = false;
        });
    },
    reopenTask() {
      this.postAction('reopen', 'Tarefa reaberta.');
    },
    postAction(action, message) {
      if (this.saving || !this.selected) {
        return;
      }
      this.saving = true;
      http.post(`/places/${this.place.id_place}/tasks/${this.selected.id}/${action}`)
        .then((response) => {
          this.selected = response.data;
          this.$emit('changed');
          alert.toastSuccess(message);
        })
        .catch((error) => alert.toastError(taskError(error)))
        .finally(() => {
          this.saving = false;
        });
    },
    downloadFile(file) {
      http.get(`/places/${this.place.id_place}/tasks/${this.selected.id}/files/${file.id}`, { responseType: 'blob' })
        .then((response) => {
          const url = URL.createObjectURL(response.data);
          window.open(url, '_blank');
        })
        .catch(() => alert.toastError('Não foi possível abrir o anexo.'));
    },
    openChart() {
      if (!this.canOpenChart) {
        return;
      }
      this.$router.push({
        name: 'MedicalRecord',
        params: { patientId: this.selected.patient.id },
        query: {
          prof_id: this.userAuth.id_prof,
          ...(this.canReply && TASK_TYPES.find((type) => type.value === this.selected.type)?.clinical ? {
            task_id: this.selected.id,
            task_type: this.selected.type,
            task_place_id: this.place.id_place,
          } : {}),
        },
      });
      this.close();
    },
  },
};
</script>

<style>
.task-dialog-lock {
  overflow: hidden;
}
</style>

<style scoped>
.task-dialog {
  --task-brand: #780f5a;
  --task-line: #e4e6ef;
  --task-muted: #7e8299;
  --task-ink: #181c32;
  --task-soft: #f8f5f7;
  --task-danger: #f1416c;
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(24, 28, 50, 0.38);
}
.task-dialog__card {
  width: min(76rem, calc(100vw - 2rem));
  max-width: none;
  max-height: calc(100vh - 2rem);
  min-height: min(40rem, calc(100vh - 2rem));
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid var(--task-line);
  border-radius: 0.85rem;
  box-shadow: 0 18px 48px rgba(24, 28, 50, 0.18);
}
.task-dialog__card.is-detail {
  width: min(40rem, calc(100vw - 2.5rem));
  min-height: 0;
}
.task-dialog__head,
.task-dialog__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-shrink: 0;
  padding: 0.85rem 1rem;
}
.task-dialog__head {
  border-bottom: 1px solid var(--task-line);
}
.task-dialog__head h3,
.task-form h4,
.task-detail__head h4 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--task-ink);
}
.task-detail__head h4 {
  margin-top: 0.25rem;
  font-size: 1.2rem;
}
.task-dialog__foot {
  justify-content: flex-end;
  flex-wrap: wrap;
  border-top: 1px solid var(--task-line);
}
.task-confirm {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(24, 28, 50, 0.45);
}
.task-confirm__card {
  width: min(26rem, 100%);
  padding: 1rem;
  background: #fff;
  border: 1px solid var(--task-line);
  border-radius: 0.85rem;
}
.task-confirm__card h3 {
  margin: 0 0 0.4rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--task-ink);
}
.task-confirm__card p {
  margin: 0 0 1rem;
  color: var(--task-ink);
}
.task-confirm__card footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.45rem;
}
.task-dialog__close {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.1rem;
  height: 2.1rem;
  margin: 0;
  padding: 0;
  border: 1px solid var(--task-line);
  border-radius: 0.5rem;
  background: #fff;
  color: var(--task-muted);
  cursor: pointer;
  box-shadow: none;
}
.task-dialog__close i {
  font-size: 0.85rem;
  line-height: 1;
}
.task-dialog__close:hover {
  border-color: var(--task-brand);
  color: var(--task-brand);
  background: var(--task-soft);
}
.task-dialog__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
}
.task-dialog__split {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(24rem, 0.95fr);
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
.task-dialog__detail {
  padding: 0.9rem 1rem 1rem;
}
.task-list {
  min-height: 0;
  overflow: auto;
  padding: 0.9rem 1rem 1rem;
}
.task-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin: 0 0 0.75rem;
  padding: 0;
  list-style: none;
}
.task-chip {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 2.4rem;
  margin: 0;
  padding: 0.45rem 0.9rem;
  border: 1px solid var(--task-line);
  border-radius: 0.5rem;
  background: #fff;
  color: var(--task-ink);
  font-weight: 600;
  font-size: 0.92rem;
  cursor: pointer;
  box-shadow: none;
}
.task-chip.is-on,
.task-chip:hover {
  border-color: var(--task-brand);
  color: var(--task-brand);
  background: var(--task-soft);
}
.task-chip--primary,
.task-chip--primary:hover {
  background: var(--task-brand);
  border-color: var(--task-brand);
  color: #fff;
}
.task-chip--primary:hover {
  background: #65104c;
  border-color: #65104c;
}
.task-chip--emission {
  background: #0f766e;
  border-color: #0f766e;
  color: #fff;
}
.task-chip--emission:hover {
  background: #115e59;
  border-color: #115e59;
  color: #fff;
}
.task-chip--emission:focus-visible {
  outline: 3px solid #5eead4;
  outline-offset: 2px;
}
.task-split {
  position: relative;
  display: inline-flex;
}
.task-split__main {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.task-split__toggle {
  padding-inline: 0.6rem;
  border-left-color: #5eead4;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.task-split__menu {
  position: absolute;
  right: 0;
  bottom: calc(100% + 0.35rem);
  z-index: 3;
  min-width: 100%;
  padding: 0.3rem;
  background: #fff;
  border: 1px solid var(--task-line);
  border-radius: 0.6rem;
  box-shadow: 0 8px 24px rgba(24, 28, 50, 0.16);
}
.task-split__menu button {
  display: block;
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 0;
  border-radius: 0.4rem;
  background: none;
  color: var(--task-ink);
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
}
.task-split__menu button:hover,
.task-split__menu button:focus-visible {
  background: var(--task-soft);
}
.task-chip--danger,
.task-chip--danger:hover {
  border-color: var(--task-danger);
  color: var(--task-danger);
  background: #fff;
}
.task-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
  margin: 0 0 0.75rem;
  padding: 1rem;
  border: 1px solid var(--task-state-line, var(--task-line));
  border-left: 4px solid var(--task-state-color, var(--task-line));
  border-radius: 0.85rem;
  background: var(--task-state-bg, #fff);
  color: var(--task-ink);
  transition: box-shadow 0.15s, border-color 0.15s;
}
.task-row--open {
  --task-state-color: #92400e;
  --task-state-line: #f5d39b;
  --task-state-bg: #fffbf3;
  --task-state-icon: #fef0cf;
}
.task-row--answered {
  --task-state-color: #166534;
  --task-state-line: #b8dfca;
  --task-state-bg: #f3fbf6;
  --task-state-icon: #dcf5e5;
}
.task-row--cancelled {
  --task-state-color: #626777;
  --task-state-line: #dfe1e7;
  --task-state-bg: #f8f9fb;
  --task-state-icon: #eceef2;
}
.task-row:hover,
.task-row:focus-within {
  border-color: var(--task-state-color, var(--task-brand));
  box-shadow: 0 3px 10px rgba(24, 28, 50, 0.08);
}
.task-row__heading {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  margin-bottom: 0.35rem;
}
.task-row__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 2.4rem;
  height: 2.4rem;
  border-radius: 0.7rem;
  background: var(--task-state-icon);
  color: var(--task-state-color);
  font-size: 1.15rem;
}
.task-row__icon i { color: inherit; }
.task-row__patient {
  color: var(--task-ink);
  font-size: 0.82rem;
  margin-top: 0.2rem;
}
.task-row__open {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.task-row__open strong {
  max-width: 100%;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.3;
}
.task-row__who {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.task-row__meta,
.task-kicker {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  max-width: 100%;
  color: var(--task-muted);
  font-size: 0.8rem;
}
.task-row__body {
  display: block;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--task-muted);
  font-size: 0.8rem;
}
.task-status {
  display: inline-flex;
  align-items: center;
  padding: 0.05rem 0.45rem;
  border-radius: 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.4;
}
.task-status--open {
  background: #fff4de;
  color: #92400e;
}
.task-status--answered {
  background: #e8fff3;
  color: #166534;
}
.task-status--cancelled {
  background: #f1f1f4;
  color: #7e8299;
}
.task-row__cancel {
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--task-danger);
  border-radius: 0.5rem;
  background: #fff;
  color: var(--task-danger);
  cursor: pointer;
}
.task-row__cancel:hover {
  background: #fff5f8;
}
.task-row__cancel:disabled {
  opacity: 0.55;
  cursor: default;
}
.task-muted {
  margin: 0;
  color: var(--task-muted);
  font-size: 0.8rem;
}
.task-hint {
  margin: -0.35rem 0 0;
  color: var(--task-muted);
  font-size: 0.8rem;
}
.task-form,
.task-reply {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.task-form {
  min-height: 0;
  overflow: auto;
  padding: 0.9rem 1rem 1.1rem;
  background: var(--task-soft);
  border-left: 1px solid var(--task-line);
}
.task-form__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
.task-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--task-muted);
}
.task-field .form-control,
.task-reply .form-control {
  min-height: 2.4rem;
  border: 1px solid var(--task-line);
  border-radius: 0.5rem;
  background: #fff;
  color: var(--task-ink);
  font-size: 0.88rem;
  font-weight: 500;
  box-shadow: none;
}
.task-field textarea.form-control,
.task-reply textarea.form-control {
  min-height: 6.25rem;
  resize: vertical;
}
.task-field .form-control:focus,
.task-reply .form-control:focus {
  border-color: var(--task-brand);
  background: #fff;
  color: var(--task-ink);
  box-shadow: none;
  outline: none;
}
.task-field .form-control::placeholder,
.task-reply .form-control::placeholder {
  color: #a1a5b7;
  font-weight: 500;
}
.task-file {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 2.4rem;
  padding: 0.4rem 0.75rem;
  border: 1px dashed var(--task-line);
  border-radius: 0.5rem;
  background: #fff;
  color: var(--task-ink);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
}
.task-file input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.task-file span {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.task-detail {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}
.task-detail__head,
.task-message,
.task-reply,
.task-detail > .task-chip {
  width: 100%;
}
.task-message {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
  margin: 0;
  padding: 0.8rem 0.9rem;
  border: 1px solid var(--task-line);
  border-radius: 0.85rem;
  background: #fff;
}
.task-message--reply {
  background: var(--task-soft);
}
.task-message header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  font-size: 0.8rem;
}
.task-message header span,
.task-message header strong {
  font-weight: 700;
}
.task-message header span {
  color: var(--task-muted);
}
.task-message p {
  margin: 0;
  color: var(--task-ink);
  white-space: pre-wrap;
}
.task-files {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.task-reply {
  padding-top: 0.35rem;
  border-top: 1px solid var(--task-line);
}
@media (max-width: 800px) {
  .task-dialog__card,
  .task-dialog__card.is-detail {
    width: min(100%, calc(100vw - 1.5rem));
    min-height: 0;
  }
  .task-dialog__split {
    grid-template-columns: 1fr;
    height: auto;
    overflow: auto;
  }
  .task-form {
    border-left: 0;
    border-top: 1px solid var(--task-line);
  }
  .task-form__grid {
    grid-template-columns: 1fr;
  }
}
</style>
